import { useCallback, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import { useWindowDimensions } from "react-native";
import { useUnistyles } from "react-native-unistyles";

import EPG_COLUMN_GAP_STEP, {
  EPG_COMPACT_ROW_HEIGHT,
} from "$src/core/constants/epg-grid";
import isIPad from "$src/core/utils/device/is-ipad";
import EPG_TV_CHANNELS_PER_PAGE from "$src/core/utils/epg/epg-tv-channels-per-page";
import CustomPlatform from "$src/core/utils/platform/platform";

const FALLBACK_EPG_GRID_HEIGHT_RATIO = 0.45;

export const useEPGChannelGridLayout = () => {
  const { theme } = useUnistyles();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  // Only the phone pages one full-width channel at a time.
  const isPagedLayout = CustomPlatform.select({
    web: false,
    tv: false,
    ios: !isIPad,
    default: true,
  });

  // Web: 360px columns with a 64px gutter (Figma desktop); TV keeps its own step.
  const columnGap = theme.spacing(
    CustomPlatform.select({ web: 16, default: EPG_COLUMN_GAP_STEP }),
  );
  const compactColumnWidth = theme.spacing(20);
  const expandedColumnWidth = theme.spacing(
    CustomPlatform.select({ web: 90, default: 78 }),
  );

  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    const width = Math.floor(e.nativeEvent.layout.width);
    const height = Math.floor(e.nativeEvent.layout.height);

    setContainerSize((prev) => {
      const nextHeight = height > 0 ? height : (prev?.height ?? 0);

      if (prev?.width === width && prev.height === nextHeight) {
        return prev;
      }

      return { width, height: nextHeight };
    });
  }, []);

  const effectiveContainerWidth = containerSize?.width || windowWidth;

  /* Below two columns web fills the width: a column plus a sliver reads as a layout fault. */
  const fitsMultipleColumns =
    effectiveContainerWidth >= expandedColumnWidth * 2 + columnGap;
  const webColumnWidth = fitsMultipleColumns
    ? expandedColumnWidth
    : Math.max(0, effectiveContainerWidth);

  // A tablet is narrower than a desktop, so its columns share the width evenly
  // instead of taking the fixed desktop width and leaving a clipped sliver.
  const tabletColumns = Math.min(
    EPG_TV_CHANNELS_PER_PAGE,
    Math.max(
      1,
      Math.floor(
        (effectiveContainerWidth + columnGap) / (theme.spacing(56) + columnGap),
      ),
    ),
  );
  const tabletColumnWidth = Math.max(
    0,
    (effectiveContainerWidth - tabletColumns * columnGap) / tabletColumns,
  );

  const multiColumnWidth = CustomPlatform.select({
    web: webColumnWidth,
    tv: expandedColumnWidth,
    ios: isIPad ? tabletColumnWidth : compactColumnWidth,
    default: compactColumnWidth,
  });

  const pageGutter = CustomPlatform.select({
    web: 0,
    // 16px side gutter on phone (KAHI-268 mobile); 8px page gutter on TV groups.
    tv: theme.spacing(2),
    default: theme.spacing(4),
  });
  const pagedPageWidth = Math.max(theme.spacing(20), effectiveContainerWidth);
  const columnWidth = isPagedLayout
    ? Math.max(0, pagedPageWidth - pageGutter * 2)
    : multiColumnWidth;
  const pageWidth = isPagedLayout
    ? pagedPageWidth
    : multiColumnWidth + columnGap;

  const fallbackGridHeight = Math.max(
    theme.spacing(70),
    Math.floor(windowHeight * FALLBACK_EPG_GRID_HEIGHT_RATIO),
  );

  const gridHeight =
    containerSize && containerSize.height > 0
      ? containerSize.height
      : fallbackGridHeight;

  const compactRowHeight = CustomPlatform.select({
    tv: theme.spacing(14),
    default: EPG_COMPACT_ROW_HEIGHT,
  });
  // Outer card margins + card border + fixed image band + program meta row.
  const liveRowHeight =
    theme.spacing(2) * 2 +
    theme.border.widths.sm * 2 +
    theme.spacing(34) +
    compactRowHeight;

  return {
    isPagedLayout,
    columnWidth,
    pageWidth,
    gridHeight,
    compactRowHeight,
    liveRowHeight,
    handleContainerLayout,
    columnGap,
    pageGutter,
    effectiveContainerWidth,
  };
};
